// ─── constants/siteSearchIndex.js ────────────────────────────────────────────
// Every static destination the global search bar can jump to — settings
// sections, dashboards, and marketing pages included. Freelancers, services,
// job postings, and courses aren't listed here; those come from /api/search
// since they're database content, not fixed routes.
//
// `auth`: 'any' shows regardless of login state, 'in' only when signed in.
// `accountTypes`, when present, further restricts to those accountType values
// (mirrors the gating already used in App.jsx's Header and Profile.jsx).
import { CATEGORY_NAMES, CATEGORIES } from './taxonomy';
import { hasInstructorAccess } from '../utils/roles';
import { HELP_CATEGORIES, searchHelpArticles } from './helpContent';

export const SITE_PAGES = [
  { label: 'Home', path: '/', keywords: 'home landing start', auth: 'any' },
  { label: 'Browse services', path: '/hire-talent', keywords: 'browse services shop hire buy', auth: 'any' },
  { label: 'Browse freelancers', path: '/browse-freelancers', keywords: 'browse freelancers talent people students', auth: 'any' },
  { label: 'Find work', path: '/find-work', keywords: 'find work jobs gigs freelance earn', auth: 'any' },
  { label: 'Job postings', path: '/job-postings', keywords: 'job postings hire jobs', auth: 'any' },
  { label: 'Academy', path: '/academy', keywords: 'academy learn education', auth: 'any' },
  { label: 'Academy courses', path: '/academy/courses', keywords: 'academy courses learn classes', auth: 'any' },
  { label: 'Academy seminars', path: '/academy/seminars', keywords: 'academy seminars live events', auth: 'any' },
  { label: 'Academy tutorials', path: '/academy/tutorials', keywords: 'academy tutorials free lessons', auth: 'any' },
  { label: 'About', path: '/about', keywords: 'about us company mission', auth: 'any' },
  { label: 'Terms of Service', path: '/terms', keywords: 'terms legal service agreement', auth: 'any' },
  { label: 'Privacy Policy', path: '/privacy', keywords: 'privacy legal data policy', auth: 'any' },
  { label: 'Help Center', path: '/help', keywords: 'help support faq questions contact troubleshooting', auth: 'any' },
  // One entry per Help Center category (Getting Started, Buying, Selling, ...)
  // so searching a topic word ("payments", "messaging") lands on that hub
  // even before matching a specific article — matchHelpArticles below covers
  // the article level.
  ...HELP_CATEGORIES.map((c) => ({
    label: `Help: ${c.label}`,
    path: `/help/${c.slug}`,
    keywords: `help ${c.label} ${c.description}`,
    auth: 'any',
  })),

  { label: 'My learning', path: '/academy/my-library', keywords: 'my learning my library my dashboard my courses enrolled saved academy learning content', auth: 'in' },
  { label: 'Inbox', path: '/inbox', keywords: 'inbox messages chat conversations dm', auth: 'in' },
  { label: 'My Work', path: '/my-listings', keywords: 'my listings services selling gigs job postings', auth: 'in' },

  { label: 'Profile overview', path: '/profile?section=overview', keywords: 'profile overview bio skills headline public', auth: 'in' },
  { label: 'Personal info', path: '/profile?section=personal', keywords: 'settings personal info name email account', auth: 'in' },
  { label: 'Account type', path: '/profile?section=account-type', keywords: 'settings account type freelancer client switch', auth: 'in' },
  { label: 'Security', path: '/profile?section=security', keywords: 'settings security password change', auth: 'in' },
  { label: 'Notifications', path: '/profile?section=notifications', keywords: 'settings notifications email preferences alerts', auth: 'in' },
  { label: 'Payouts', path: '/profile?section=payouts', keywords: 'settings payouts stripe payment bank money', auth: 'in', accountTypes: ['member'] },
  { label: 'Academy settings', path: '/profile?section=academy', keywords: 'settings academy learning membership', auth: 'in' },

  { label: 'Instructor dashboard', path: '/instructor/dashboard', keywords: 'instructor dashboard teaching grading submissions courses', auth: 'in', requiresInstructor: true },

  { label: 'Post a job', path: '/job-postings/create', keywords: 'post a job create hire new', auth: 'in' },
  { label: 'Create a listing', path: '/find-work/create-listing', keywords: 'create listing service sell offer new', auth: 'in' },
  { label: 'Create a course', path: '/academy/create/course', keywords: 'create course teach academy new', auth: 'in', requiresInstructor: true },
];

function normalize(str) {
  return String(str || '').toLowerCase();
}

function queryWords(query) {
  return normalize(query).trim().split(/\s+/).filter(Boolean);
}

// Scores a label (+ optional keywords) against every word in the query.
// Every word has to appear somewhere for any match at all — a straight
// substring check on the whole phrase fails on cases like "graphic design"
// against "Graphics & Design": the "s &" in the stored label breaks a literal
// substring match even though every word the user typed is obviously there.
function scoreMatch(label, keywords, words) {
  const l = normalize(label);
  const combined = keywords ? `${l} ${normalize(keywords)}` : l;
  if (!words.every((w) => combined.includes(w))) return 0;
  if (l.startsWith(words[0]) && words.every((w) => l.includes(w))) return 3;
  if (words.every((w) => l.includes(w))) return 2;
  return 1; // matched only via keywords, not the label itself
}

// Visible pages for this user, ranked: label-starts-with beats label-contains
// beats a keyword-only hit, so typing "sec" surfaces Security before a page
// that merely mentions "security" in its keywords.
export function matchSitePages(query, user, limit = 6) {
  const words = queryWords(query);
  if (!words.length) return [];

  const visible = SITE_PAGES.filter((p) => {
    if (p.auth === 'in' && !user) return false;
    if (p.requiresInstructor && !hasInstructorAccess(user)) return false;
    if (p.accountTypes && !p.accountTypes.includes(user?.accountType)) return false;
    return true;
  });

  return visible
    .map((p) => ({ page: p, score: scoreMatch(p.label, p.keywords, words) }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((x) => x.page);
}

// Taxonomy categories/subcategories as searchable destinations — typing a
// category name should behave exactly like clicking it in the Browse Services
// filter rail, not just turn up listings that happen to mention it in their
// title. Category hits outrank subcategory hits at an equal score, since
// landing one click higher (the whole category, already expanded) beats
// landing on one specific subcategory the user didn't necessarily ask for.
export function matchCategories(query, limit = 5) {
  const words = queryWords(query);
  if (!words.length) return [];

  const hits = [];
  CATEGORY_NAMES.forEach((cat) => {
    const catScore = scoreMatch(cat, '', words);
    if (catScore > 0) hits.push({ score: catScore + 0.5, category: cat, subcategory: null, label: cat });
    (CATEGORIES[cat] || []).forEach((sub) => {
      const subScore = scoreMatch(sub, '', words);
      if (subScore > 0) hits.push({ score: subScore, category: cat, subcategory: sub, label: `${sub} (${cat})` });
    });
  });

  return hits.sort((a, b) => b.score - a.score).slice(0, limit);
}

// Help Center articles — reuses the Help Center's own substring search over
// article title/summary/body rather than re-indexing that content here, then
// shapes the hits like every other search-result group (id/label/sub/path).
export function matchHelpArticles(query, limit = 5) {
  if (!query.trim()) return [];
  return searchHelpArticles(query)
    .slice(0, limit)
    .map(({ category, article }) => ({
      id: `${category.slug}/${article.slug}`,
      label: article.title,
      sub: article.summary,
      path: `/help/${category.slug}/${article.slug}`,
    }));
}

// A handful of shortcuts shown when the field is focused but empty — the
// destinations someone is most likely hunting for without knowing this bar
// reaches that far.
export function defaultQuickLinks(user) {
  const wanted = user
    ? ['Academy', 'My learning', 'Inbox', 'Profile overview', 'Browse services']
    : ['Browse services', 'Browse freelancers', 'Find work', 'Academy'];
  return SITE_PAGES.filter((p) => wanted.includes(p.label));
}
